import type { IntlShape } from 'react-intl';

import { EntityState } from 'src/apiClients/queries/entity-state';
import type { DataTableCellValue } from 'src/components/data-table/DataTable.types';

import { tableI18n } from './table-formatters.i18n';

export const ADDON_PRODUCT_NAMES: Record<string, string> = {
    'IMAGO-S': 'Imago Standard',
    'IMAGO-E': 'Imago Enterprise',
    'IMAGO-J': 'Imago Junior',
    'IMAGO-CAPACITY-UNITS': 'Seequent Monthly Capacity Units',
    'IMAGO-S-H': 'Imago Standard Hibernation',
    'IMAGO-E-H': 'Imago Enterprise Hibernation',
    'IMAGO-J-H': 'Imago Junior Hibernation',
    'EXTRA-STORAGE': 'Imago Extra Storage - 1 TB/Workspace',
    'MOBILE-CAPTURE': 'Imago Mobile Capture',
    'IMAGO-EAP': 'Imago Early Access Programme',
    'IMAGO-DATA-SHARING': 'Imago Data Sharing',
    'CAPTURE-CHIP': 'Imago Capture - Chip Shot',
    'CAPTURE-CORE': 'Imago Capture - Core Shot',
};

/** Returns a formatter that replaces null/undefined/empty with "N/A". */
export const createNaFallback =
    (intl: IntlShape) =>
    (value: DataTableCellValue): string => {
        if (value === null || value === undefined || value === '') {
            return intl.formatMessage(tableI18n.notAvailable);
        }
        return String(value);
    };

/** Returns a formatter that maps A/I/X state codes to labels. */
export const createStateLabel =
    (intl: IntlShape) =>
    (value: DataTableCellValue): string => {
        if (value === EntityState.ACTIVE) return intl.formatMessage(tableI18n.active);
        if (value === EntityState.INACTIVE) return intl.formatMessage(tableI18n.inactive);
        if (value === EntityState.ARCHIVED) return intl.formatMessage(tableI18n.archived);
        return intl.formatMessage(tableI18n.notAvailable);
    };

/** Standard filter options for the A/I/X state column. */
export const createStateFilterOptions = (intl: IntlShape) => [
    { label: intl.formatMessage(tableI18n.active), value: EntityState.ACTIVE },
    { label: intl.formatMessage(tableI18n.inactive), value: EntityState.INACTIVE },
    { label: intl.formatMessage(tableI18n.archived), value: EntityState.ARCHIVED },
];

/** Returns a formatter that displays ISO datetime strings in locale-appropriate format. */
export const createDateTimeFormatter = (intl: IntlShape) => {
    const naFallback = createNaFallback(intl);
    return (value: DataTableCellValue): string => {
        if (value === null || value === undefined || value === '') return naFallback(value);
        const date = new Date(String(value));
        if (Number.isNaN(date.getTime())) return naFallback(null);
        return intl.formatDate(date, { dateStyle: 'medium', timeStyle: 'short' });
    };
};

/** Returns a formatter for workspace access levels (private/public). */
export const createAccessLabel = (intl: IntlShape) => {
    const naFallback = createNaFallback(intl);
    return (value: DataTableCellValue): string => {
        if (value === 'public') return intl.formatMessage(tableI18n.accessPublic);
        if (value === 'private') return intl.formatMessage(tableI18n.accessPrivate);
        return naFallback(value);
    };
};

/** Standard filter options for workspace access levels. */
export const createAccessFilterOptions = (intl: IntlShape) => [
    { label: intl.formatMessage(tableI18n.accessPublic), value: 'public' },
    { label: intl.formatMessage(tableI18n.accessPrivate), value: 'private' },
];
